import React from "react";
import { QueryRenderer } from "@cubejs-client/react";
import { Spin, Typography } from "antd";
import moment from "moment-timezone";
import {
  getLocalPhoneNumber,
  getAnalyticsValue,
  replaceBraces,
  getWeekday,
  calculateTableSummaryRow,
} from "../../components/util/helpers";
import { CheckCircleOutlined } from "@ant-design/icons";
import {
  Chart,
  Axis,
  Tooltip,
  Geom,
  Coord,
  Legend,
  DonutChart,
} from "bizcharts";
import { Row, Col, Statistic, Table } from "antd";
import { useIntl } from "react-intl";
import _ from "lodash";
const { Text } = Typography;

// Custom formatter function
const formatNumberWithCommas = (value) => {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
    value
  );
};

const stackedChartData = (resultSet, intl) => {
  const data = resultSet
    .pivot()
    .map(({ xValues, yValuesArray }) =>
      yValuesArray.map(([yValues, m]) => {
        const lXValues = xValues.map((x) =>
          intl.formatMessage({
            id: `app.analytics.${x}`,
            defaultMessage: `${x}`,
          })
        );

        let x = resultSet.axisValuesString(lXValues, ", ");
        if (moment(x, moment.ISO_8601).isValid()) {
          x = moment(x).format("MMM");
        }

        const lYValues = yValues.map((y) =>
          intl.formatMessage({
            id: `app.analytics.${y}`,
            defaultMessage: `${y}`,
          })
        );
        return {
          x:
            x !== "null"
              ? getAnalyticsValue(intl, x)
              : intl.formatMessage({
                  id: `app.analytics.values.none`,
                }),
          color: resultSet.axisValuesString(lYValues, ", "),
          measure: m && Number.parseFloat(m),
        };
      })
    )
    .reduce((a, b) => a.concat(b), []);
  return data;
};

const getColumnRender = (column, intl) => (text) => {
  switch (column.type) {
    case "time":
      return (
        text && (
          <Text>
            {moment(text).isValid()
              ? moment.utc(text).tz("Asia/Jerusalem").format("DD/MM/YYYY")
              : ""}
          </Text>
        )
      );
    case "boolean":
      return text ? <CheckCircleOutlined /> : null;
    case "number":
      return text ? (
        <Text>
          {column.meta && column.meta.time ? (
            moment(text).isValid() ? (
              moment.utc(text).tz("Asia/Jerusalem").format("DD/MM/YYYY")
            ) : (
              ""
            )
          ) : (
            <span style={{ direction: "ltr", unicodeBidi: "bidi-override" }}>
              {(Math.round(text * 100) / 100).toString()}{" "}
              {column.format === "percent" ? "%" : ""}
            </span>
          )}
        </Text>
      ) : (
        ""
      );

    default:
      return (
        <Text>
          {column.meta && column.meta.phone
            ? getLocalPhoneNumber(text)
            : column.meta && column.meta.email
            ? text
            : column.meta && column.meta.weekday
            ? getWeekday(text)
            : text
            ? getAnalyticsValue(intl, text)
            : ""}
        </Text>
      );
  }
};

const table = ({ resultSet, pivotConfig, intl, support_totals }) => {
  let columns = resultSet.tableColumns(pivotConfig);
  const annotation = resultSet.annotation();
  const isEmpty = _.isEmpty(annotation.timeDimensions);

  if (!isEmpty) {
    const timeDimensionKey = Object.keys(annotation.timeDimensions)[0];
    const filtered = columns.find((i) => i.key === timeDimensionKey);
    if (filtered) {
      const shorten = filtered.key.substr(0, filtered.key.lastIndexOf("."));
      columns = [
        ...columns.filter((i) => i.key !== timeDimensionKey),
        { ...filtered, key: shorten },
      ];
    }
  }
  columns = columns.map((column) => ({
    ...column,
    title: intl.formatMessage({
      id: `app.analytics.${column.key}`,
      defaultMessage: `${column.shortTitle}`,
    }),
    render: getColumnRender(column, intl),
  }));

  let dataSource = resultSet.tablePivot(pivotConfig);

  // Add summary row if support_totals is true
  if (support_totals) {
    const summaryRow = calculateTableSummaryRow(columns, dataSource);
    summaryRow.isSummaryRow = true; // Add flag to identify summary row
    dataSource = [...dataSource, summaryRow];
  }

  return (
    <Table
      bordered
      pagination={{ pageSize: 100 }}
      columns={columns}
      dataSource={dataSource}
      style={{ height: "max-content" }}
      scroll={{ y: window.innerHeight - 406 }}
      rowClassName={(record) => (record.isSummaryRow ? "summary-row" : "")}
      components={{
        body: {
          row: ({ className, ...props }) => (
            <tr
              {...props}
              className={`${className || ""}`}
              style={
                className?.includes("summary-row")
                  ? {
                      backgroundColor: "#f0f0f0",
                      fontWeight: "bold",
                    }
                  : {}
              }
            />
          ),
        },
      }}
    />
  );
};

const chartTypeToTemplate = {
  line: ({ resultSet, intl }) => {
    const data = stackedChartData(resultSet, intl);
    return (
      <Chart
        autoFit
        scale={{ x: { tickCount: 8 } }}
        height={400}
        data={data}
        forceFit
      >
        <Axis name="x" />
        <Axis name="measure" />
        <Tooltip crosshairs={{ type: "y" }} />
        <Geom type="line" position={"x*measure"} size={2} color="color" />
      </Chart>
    );
  },
  bar: ({ resultSet, intl }) => (
    <Chart
      pure={true}
      scale={{ x: { tickCount: 8 } }}
      autoFit
      data={stackedChartData(resultSet, intl)}
      forceFit
      height={400}
    >
      <Axis name="x" />
      <Axis name="measure" />

      <Tooltip
        customItems={(originalItems) => {
          return originalItems.map((item) => {
            // Format the value by adding a thousands separator
            const formattedValue = formatNumberWithCommas(item.value);
            // Return a new TooltipItem with the formatted value
            return {
              ...item,
              value: formattedValue,
            };
          });
        }}
      />
      <Geom type="interval" position={"x*measure"} color="color" />
    </Chart>
  ),
  area: ({ resultSet, intl }) => (
    <Chart
      pure={true}
      autoFit
      scale={{ x: { tickCount: 8 } }}
      height={400}
      data={stackedChartData(resultSet, intl)}
      forceFit
    >
      <Axis name="x" />
      <Axis name="measure" />

      <Geom type="interval" position={"x*measure"} color="color" />
    </Chart>
  ),
  pie: ({ resultSet, intl }) => {
    return (
      <DonutChart
        pure={true}
        autoFit
        data={stackedChartData(resultSet, intl)}
        forceFit
        statistic={{
          visible: true,
          totalLabel: intl.formatMessage({
            id: "app.analytics.total",
          }),
        }}
        radius={0.8}
        padding="auto"
        angleField="measure"
        colorField="x"
        pieStyle={{ stroke: "white", lineWidth: 5 }}
      />
    );
  },
  number: ({ resultSet, title }) => (
    <Row type="flex" justify="center" align="middle" style={{ height: "100%" }}>
      <Col>
        {resultSet.seriesNames().map((s) => (
          <Statistic
            key={s.key}
            title={title}
            value={
              resultSet.totalRow()[s.key] ? resultSet.totalRow()[s.key] : 0
            }
          />
        ))}
      </Col>
    </Row>
  ),
  table,
};
const renderChart =
  (Component, pivotConfig, intl) =>
  ({ resultSet, error, title, support_totals }) => {
    if (resultSet != null) {
      // console.log(JSON.stringify(resultSet.chartPivot()));
    }
    return (
      (resultSet && (
        <div dir="ltr" style={{ height: "100%", width: "100%" }}>
          <Component
            resultSet={resultSet}
            pivotConfig={pivotConfig}
            title={title}
            intl={intl}
            support_totals={support_totals}
          />
        </div>
      )) ||
      (error && error.toString()) || <Spin />
    );
  };

const ChartRenderer = ({ query, cubejsApi, chartType, pivotConfig }) => {
  const intl = useIntl();
  return (
    <div style={{ height: "100%", padding: "20px" }}>
      <QueryRenderer
        query={query}
        cubejsApi={cubejsApi}
        resetResultSetOnChange={false}
        render={renderChart(chartTypeToTemplate[chartType], pivotConfig, intl)}
      />
    </div>
  );
};
export const ResultSetRenderer = ({
  chartType,
  pivotConfig,
  resultSet,
  error,
  name,
  support_totals,
}) => {
  const intl = useIntl();
  const Component = renderChart(
    chartTypeToTemplate[chartType],
    pivotConfig,
    intl
  );
  return (
    <Component
      resultSet={resultSet}
      error={error}
      title={name}
      support_totals={support_totals}
    />
  );
};

export default ChartRenderer;
